import * as Y from 'yjs';
import { boardFileToDoc, encodeVersion, SceneStore } from '@os/doc';
import type { Api } from '../../lib/api';
import type { UploadState } from '../media/uploads';
import { readBoardArchive, type BoardArchive } from './archive';

/** What an import needs of a board's media session (P4): store an image as an asset, then wait for its upload. */
export type ImportSession = {
  importAsset(file: File): Promise<string | null>;
  queue: { settled(ids: readonly string[]): Promise<void>; entries(): { assetId: string; state: UploadState }[] };
  destroy(): void;
};

const ext = (mime: string) => (mime === 'image/gif' ? 'gif' : mime === 'image/png' ? 'png' : mime === 'image/jpeg' ? 'jpg' : 'webp');

/** A file written elsewhere names its images by URL: only network URLs are fetched (never data:, blob:, file:, javascript:). */
const fetchable = (url: string | null): url is string => {
  try {
    // Relative URLs resolve against the page, as fetch would: same-origin http(s).
    return url !== null && /^https?:$/.test(new URL(url, globalThis.location?.href).protocol);
  } catch {
    return false;
  }
};

const defaultFetch = async (url: string): Promise<Blob | null> => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    return res.ok ? await res.blob() : null;
  } catch {
    return null;
  }
};

/** Points the document's images and thumbnails at the uploaded asset (one transaction, before the upload ends). */
function retarget(doc: Y.Doc, from: string, to: string): void {
  doc.transact(() => {
    for (const m of doc.getMap<Y.Map<unknown>>('objects').values()) {
      if (m.get('type') === 'image' && m.get('assetId') === from) m.set('assetId', to);
      if (m.get('thumbAssetId') === from) m.set('thumbAssetId', to);
    }
  });
}

/**
 * A `.osboard` or `BoardFile` JSON imported as a new board of the project (spec §12, plan P9 Rulings 2 and 10): the
 * file is validated before anything is created; assets the organization already has (same SHA-256) are reused, the
 * others go through the P4 pipeline; the board is seeded by its room once every upload has ended. Any failure after
 * the board exists moves it to the trash.
 */
export async function importBoard(o: {
  api: Pick<Api, 'createBoard' | 'checkAsset' | 'seedBoard' | 'lifecycle' | 'updateBoard'>;
  orgId: string;
  projectId: string;
  /** A `.osboard` or JSON file, or a file already read (a Figma import, P11b). */
  input: File | BoardArchive;
  session(boardId: string, store: SceneStore): ImportSession;
  fetchBlob?(url: string): Promise<Blob | null>;
  onProgress?(step: 'read' | 'assets' | 'seed', done: number, total: number): void;
  /** Stops the import before the board is seeded; the board then goes to the trash. */
  signal?: AbortSignal;
  /** A board the caller already created (P11b Ruling 6): filled instead of a new one, renamed `title` once seeded. */
  board?: { id: string; title: string };
}): Promise<{ boardId: string; objects: number; skipped: number; missing: number; failed: number }> {
  o.onProgress?.('read', 0, 1);
  const archive = o.input instanceof File ? await readBoardArchive(o.input) : o.input;
  const { file } = archive;
  const name = o.input instanceof File ? o.input.name.replace(/\.[^.]+$/, '').slice(0, 200) : '';
  const board = o.board ? { id: o.board.id } : await o.api.createBoard({ orgId: o.orgId, projectId: o.projectId, title: file.title.trim() || name || 'Board' });
  let session: ImportSession | null = null;
  try {
    o.signal?.throwIfAborted();
    const reuse = new Map<string, string>();
    for (const a of file.assets) {
      const hit = a.sha256 ? await o.api.checkAsset(board.id, a.sha256) : null;
      if (hit) reuse.set(a.id, hit);
    }
    const store = new SceneStore(boardFileToDoc(file, reuse), { captureTimeout: 0 });
    session = o.session(board.id, store);
    const uploads: string[] = [];
    let missing = archive.missing.filter((id) => !file.assets.some((a) => a.id === id)).length; // not listed in the file
    const todo = file.assets.filter((a) => !reuse.has(a.id));
    for (const [i, a] of todo.entries()) {
      const blob = archive.blobs.get(a.id) ?? (fetchable(a.url) ? await (o.fetchBlob ?? defaultFetch)(a.url) : null);
      const id = blob ? await session.importAsset(new File([blob], `${a.id}.${ext(blob.type || a.mime)}`, { type: blob.type || a.mime })) : null;
      if (id) {
        retarget(store.doc, a.id, id);
        uploads.push(id);
      } else missing++;
      o.onProgress?.('assets', i + 1, todo.length);
      o.signal?.throwIfAborted();
    }
    await session.queue.settled(uploads);
    o.signal?.throwIfAborted();
    const failed = session.queue.entries().filter((e) => uploads.includes(e.assetId) && e.state !== 'done').length;
    o.onProgress?.('seed', 0, 1);
    await o.api.seedBoard(board.id, await encodeVersion(store.doc));
    if (o.board) await o.api.updateBoard(board.id, { title: o.board.title.trim().slice(0, 200) || 'Board' });
    return { boardId: board.id, objects: file.objects.length, skipped: archive.skipped, missing, failed };
  } catch (err) {
    await o.api.lifecycle('board', 'trash', board.id).catch(() => undefined);
    throw err;
  } finally {
    session?.destroy();
  }
}
